/* globals window, document */
import { reactive } from 'vue'
import { is } from './util'

function loadScript(appId) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src*="widget.intercom.io/widget/${appId}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    script.onload = resolve
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript.parentNode.insertBefore(script, firstScript)
  })
}

// Messenger settings that depend on the viewport. One source for the boot
// options, window.intercomSettings and later updates, so the lightweight
// launcher, the full messenger and a resize all agree.
//
// Below the app's mobile breakpoint (760px, the same line App.vue draws for
// store.smallScreen) the default launcher is hidden: the bottom action bar
// already carries "Chat with support", and the bubble sat on top of the
// bar, spending screen space a phone does not have. The messenger itself
// still opens through the API (show / showMessages / showNewMessage) and
// Intercom still delivers in-app messages; only the bubble goes.
export function intercomLayoutSettings() {
  const small = window.innerWidth < 760
  return {
    horizontal_padding: 20,
    vertical_padding: small ? 100 : 20,
    hide_default_launcher: small
  }
}

export default {
  install(app, { appId }) {
    const state = reactive({
      ready: false,
      // True once boot() has been dispatched. Re-booting an already booted
      // messenger tears it down and re-creates it, closing anything open:
      // callers that boot "just in case" (Monitoring's anonymous boot when
      // `contacted` flips) check this first. An identified boot at sign-in
      // still re-boots on purpose, to carry the user hash.
      booted: false,
      visible: false,
      unreadCount: 0
    })

    let loadPromise = null

    const ensureLoaded = () => {
      if (state.ready) return Promise.resolve()
      if (!loadPromise) {
        // Set up the Intercom placeholder queue so calls made before
        // the script loads are buffered and replayed automatically
        if (typeof window.Intercom !== 'function') {
          const placeholder = (...args) => placeholder.c(args)
          placeholder.q = []
          placeholder.c = args => placeholder.q.push(args)
          window.Intercom = placeholder
        }
        loadPromise = loadScript(appId).then(() => {
          state.ready = true
          // body.intercom-open: index.html lifts body's CSS filter under it
          // on phones, where the filter blanked the messenger iframe.
          window.Intercom('onHide', () => { state.visible = false; document.body.classList.remove('intercom-open') })
          window.Intercom('onShow', () => { state.visible = true; document.body.classList.add('intercom-open') })
          window.Intercom('onUnreadCountChange', count => (state.unreadCount = count))
        })
      }
      return loadPromise
    }

    const callIntercom = (...args) => window.Intercom(...args)

    // The boot in flight (or the last one). Opening calls await it so a
    // show() that lands while the identified boot is still waiting for the
    // widget script queues behind it instead of booting anonymously.
    let bootPromise = null

    const intercom = {
      get ready() { return state.ready },
      get booted() { return state.booted },
      get visible() { return state.visible },
      get unreadCount() { return state.unreadCount },

      _call: callIntercom,

      // Identified boots (user_id present) always go through: that is how
      // Nav re-identifies at sign-in. An ANONYMOUS boot over a session that
      // is already booted (or booting) is dropped: Intercom would replace
      // the identified visitor with an anonymous one, which is how a
      // signed-in user on a phone got the email prompt — the tap on "Chat
      // with support" landed while the identified boot was still waiting
      // for the widget script, `booted` was not yet set, and the opener's
      // fallback boot won. `booted` is therefore claimed synchronously, at
      // the call, not after the script arrives.
      boot(options = { app_id: appId }) {
        const identified = !!(options && options.user_id)
        if (state.booted && !identified) return bootPromise || Promise.resolve()
        state.booted = true
        bootPromise = (async () => {
          await ensureLoaded()
          const layout = intercomLayoutSettings()
          window.intercomSettings = { ...(window.intercomSettings || {}), ...layout }
          options = { ...layout, ...options }
          if (!options.app_id) options.app_id = appId
          callIntercom('boot', options)
        })()
        return bootPromise
      },
      // Boot if nothing has, otherwise wait for the boot in flight.
      async _ensureBooted() {
        if (!state.booted) return intercom.boot()
        if (bootPromise) await bootPromise
      },
      // Re-apply the viewport settings after a breakpoint change (the
      // launcher shows or hides, the padding follows). No-op until booted.
      updateLayout: () => { if (state.ready && state.booted) callIntercom('update', intercomLayoutSettings()) },
      shutdown: () => { if (state.ready) { callIntercom('shutdown'); state.booted = false; bootPromise = null } },
      update: (...options) => { if (state.ready) callIntercom('update', ...options) },
      showArticle: (article) => { if (state.ready) callIntercom('showArticle', article) },
      // Opening calls boot first when nothing has: Intercom drops show /
      // showMessages / showNewMessage before a boot, and on every page but
      // the FrontPage nobody boots for a signed-out visitor (Monitoring only
      // mounts once `contacted` or a user exists, and only boots identified
      // users that carry a hash). An anonymous boot is the right fallback;
      // Nav re-boots identified when the hash arrives.
      async show() {
        await intercom._ensureBooted()
        callIntercom('show')
      },
      hide: () => { if (state.ready) callIntercom('hide') },
      async showMessages() {
        await intercom._ensureBooted()
        callIntercom('showMessages')
      },
      async showNewMessage(content) {
        await intercom._ensureBooted()
        callIntercom('showNewMessage', ...(is(String, content) ? [content] : []))
      },
      trackEvent: (name, ...metadata) => { if (state.ready) callIntercom('trackEvent', ...[name, ...metadata]) },
      getVisitorId: () => state.ready ? callIntercom('getVisitorId') : null
    }

    app.config.globalProperties.$intercom = intercom
  }
}
